const chipSeq = {
  title: "ChIP-Seq Documentation",

  fields: [
    {
      title: "Project Name",

      description:
        "Provide a meaningful and unique name for your ChIP-Seq project. This name will be used to identify the analysis throughout the platform.",

      examples: [
        "CTCF_Binding_Study",
        "H3K27ac_ChIPSeq",
        "Breast_Cancer_ChIP",
      ],

      notes: [
        "Choose a descriptive project name.",
        "Avoid using special characters where possible.",
        "The project name will appear in your dashboard and reports.",
      ],
    },

    {
      title: "Number of Samples",

      description:
        "Enter the total number of sequencing samples included in this analysis. Credits will be calculated automatically based on the number of samples.",

      examples: [
        "1",
        "4",
        "12",
      ],

      notes: [
        "Must be greater than zero.",
        "Ensure the number matches the Metadata Builder entries.",
      ],
    },

    {
      title: "Short Notes",

      description:
        "Provide any additional information that may help the bioinformatics team understand your experiment or special analysis requirements.",

      examples: [
        "H3K27ac ChIP performed on liver tissue.",
        "Samples sequenced using Illumina NovaSeq.",
      ],

      notes: [
        "Optional field.",
        "Mention any special instructions if required.",
      ],
    },

    {
      title: "Raw Data Source",

      description:
        "Specify how your sequencing data will be provided for analysis.",

      examples: [
        "Online",
        "Offline",
      ],

      notes: [
        "For Online, provide the download link to your sequencing files.",
        "Username and password can be provided if the link is protected.",
        "If Offline is selected, our team will contact you regarding the data transfer process.",
      ],
    },

    {
      title: "Organism",

      description:
        "Select the reference genome that matches your ChIP-Seq experiment. The selected organism determines the reference genome and annotation resources used during analysis.",

      examples: [
        "Homo sapiens (hg38)",
        "Mus musculus (mm39)",
      ],

      notes: [
        "Search by typing at least three characters.",
        "Select the organism that matches your sequencing experiment.",
        "Choosing the correct organism ensures accurate alignment and peak annotation.",
      ],
    },

    {
      title: "Alignment Tool",

      description:
        "Select the alignment software used to map sequencing reads to the selected reference genome.",

      examples: [
        "BWA",
        "Bowtie",
        "Bowtie2",
      ],

      notes: [
        "BWA is recommended for most ChIP-Seq datasets.",
        "Bowtie and Bowtie2 are also supported.",
      ],
    },

    {
      title: "Read Type",

      description:
        "Specify whether the sequencing data consists of Single-End or Paired-End reads.",

      examples: [
        "Single End",
        "Paired End",
      ],

      notes: [
        "Select the option matching your sequencing data.",
        "Incorrect selection may affect downstream analysis.",
      ],
    },

    {
      title: "Metadata Builder",

      description:
        "Enter metadata for every sequencing sample included in the experiment. Each sample requires a unique Sample ID and an IP classification.",

      examples: [
        "Sample_001 | IP | CONTROL",
        "Sample_002 | IP | TREATED",
        "Input_001 | IgG / Genomic Input",
      ],

      notes: [
        "Each Sample ID must be unique.",
        "The IP column specifies whether the sample is an IP sample or an Input/Control sample.",
        "Only one 'IgG / Genomic Input' sample is allowed.",
        "Condition is required only for samples marked as 'IP'.",
        "Condition is automatically disabled for 'IgG / Genomic Input'.",
        "Click 'Add Sample' to include additional samples.",
        "Click 'Save Metadata' after entering all samples.",
        "Use 'Modify Metadata' to edit the metadata later.",
      ],
    },

    {
      title: "Comparison Builder",

      description:
        "Define the experimental comparisons used for downstream differential peak analysis. Comparisons can either be generated automatically or created manually.",

      sections: [
        {
          title: "Automatic Comparisons",

          description:
            "Automatically generates every possible comparison using the unique experimental conditions assigned to IP samples.",

          notes: [
            "Requires at least two unique IP conditions.",
            "IgG / Genomic Input samples are excluded from comparisons.",
            "Automatically creates all valid Test vs Control combinations.",
            "You can switch back to Manual mode at any time.",
          ],
        },

        {
          title: "Manual Comparisons",

          description:
            "Allows you to manually define Test and Control conditions for differential peak analysis.",

          notes: [
            "Only conditions assigned to IP samples are available.",
            "Duplicate comparisons are not allowed.",
            "Click 'Add' to include a comparison.",
            "Click 'Save Comparisons' after finalizing the list.",
            "Use 'Modify Comparisons' to edit later.",
          ],
        },
      ],
    },

    {
      title: "Raw Data QC Parameters",

      description:
        "Configure the quality control parameters applied to sequencing reads before downstream ChIP-Seq analysis.",

      sections: [
        {
          title: "Phred Quality Score",

          description:
            "Defines the minimum acceptable sequencing quality score during quality filtering.",

          notes: [
            "Allowed range: 20–40.",
            "Default value: 30.",
            "Higher values perform stricter quality filtering.",
          ],
        },

        {
          title: "Minimum Read Length",

          description:
            "Defines the minimum read length that must remain after trimming for a read to be retained.",

          notes: [
            "Reads shorter than the specified value will be discarded.",
          ],
        },
      ],
    },

    {
      title: "Peak Calling Parameters",

      description:
        "Configure the parameters used during genomic peak detection.",

      sections: [
        {
          title: "Peak Type",

          description:
            "Select the expected enrichment profile for the experiment.",

          notes: [
            "Choose Narrow peaks for transcription factor ChIP-Seq experiments.",
            "Choose Broad peaks for histone modification ChIP-Seq experiments.",
          ],
        },

        {
          title: "Significance Threshold",

          description:
            "Specify the statistical cutoff used to identify significant peaks.",

          notes: [
            "Choose either p-value or q-value.",
            "Lower threshold values produce more stringent peak calls.",
            "Typical values range from 0.00001 to 0.05.",
          ],
        },
      ],
    },

    {
      title: "Motif Enrichment Parameters",

      description:
        "Configure the parameters used for transcription factor motif enrichment analysis after peak calling.",

      sections: [
        {
          title: "Motif Database",

          description:
            "Select the motif database appropriate for your organism and analysis.",

          notes: [
            "JASPAR CORE databases are recommended for most analyses.",
            "Use organism-specific databases whenever available.",
            "HOCOMOCO databases are recommended for Human and Mouse datasets.",
          ],
        },

        {
          title: "Significance Threshold (p-value)",

          description:
            "Specify the statistical cutoff for reporting significantly enriched motifs.",

          notes: [
            "Lower values provide more stringent motif enrichment results.",
            "Typical threshold: 0.05.",
          ],
        },
      ],
    },
  ],
};

export default chipSeq;